import type { StateContext, Store } from '@ngxs/store';
import { Control, type DynamicFlatNode, type IChannel, isSameControlAsId, type StoreControl, type TelemetryDataModel, type TelemetryDataValues } from 'commons';
import { saveAs } from 'file-saver';
import { produce } from 'immer';
import { firstValueFrom } from 'rxjs';
import { addToIndexedDB } from '../../../../state_utils';
import { convertJsonToCsv, exportDataAsJson, prefixObjectKeysWith } from '../../../databrowser/cloud-browser/utils';
import { db } from '../../../db';
import { GenerateEChartConfig, GetChannelComments, GetChannelData, SetDateRangeByData } from '../actions';
import { disableLoading } from '../functions/toggleLoading';
import type { MultiViewStateModel } from '../MultiViewStateModel';
import { MultiViewState } from '../multi-view.state';
import { getViewIndex } from '.';

/**
 * Validates and extracts channel information for batch processing
 */
export function validateAndExtractChannels(channelIds: string[], tree: DynamicFlatNode<any>[]) {
  const validChannelIds: string[] = [];
  const validChannelMap = new Map<string, string>();
  const channelNodes: DynamicFlatNode<IChannel>[] = [];

  for (const id of channelIds) {
    const channel_node = tree.find(isSameControlAsId(id));
    if (!channel_node) {
      console.warn(`Channel doesn't exist: ${id}`);
      continue;
    }
    validChannelIds.push(id);
    validChannelMap.set(id, channel_node.label);
    channelNodes.push(channel_node as DynamicFlatNode<IChannel>);
  }

  return { validChannelIds, validChannelMap, channelNodes };
}

/**
 * Filters and transforms raw channel values for a specific channel
 */
export function filterChannelValues(rawValues: TelemetryDataValues[], label: string) {
  return rawValues
    .map((value) => {
      const filteredValue: Partial<TelemetryDataValues> = {};

      // Always include datetime/timestamp fields
      if (value.datetime) {
        filteredValue.datetime = value.datetime;
      }

      // Include properties that match the channel label or contain it
      Object.keys(value).forEach((key) => {
        if (key.includes(label) || key === label) {
          filteredValue[key] = value[key];
        }
        // Preserve time-related and identifier fields
        if (key.toLowerCase().includes('time') || key.toLowerCase().includes('date') || key === 'index' || key === 'id') {
          filteredValue[key] = value[key];
        }
      });

      return filteredValue;
    })
    .filter((value) => value.datetime && value[label] !== undefined && value[label] !== null);
}

/**
 * Creates a properly structured channel response
 */
export function createChannelResponse(id: string, channelSpecificValues: TelemetryDataValues[], response: TelemetryDataModel) {
  return {
    windowSize: response.windowSize,
    windowUnit: response.windowUnit,
    aggregationMethod: response.aggregationMethod,
    valuesCount: channelSpecificValues.length,
    startDateValues: response.startDateValues,
    endDateValues: response.endDateValues,
    startDateRequested: response.startDateRequested,
    endDateRequested: response.endDateRequested,
    channelValues: [
      {
        values: channelSpecificValues,
        baseChannelGroup: id,
        valuesCount: channelSpecificValues.length,
        startDate: response.startDateValues ?? '',
        endDate: response.endDateValues ?? '',
      },
    ],
  };
}

/**
 * Processes batch response and distributes data to individual channels
 */
export function processBatchResponse(
  ctx: StateContext<MultiViewStateModel>,
  response: any,
  validChannelMap: Map<string, string>,
  channelNodes: DynamicFlatNode<IChannel>[],
  current_view_index: number
) {
  if (!response?.channelValues) {
    return;
  }

  validChannelMap.forEach(processChannelGroupValues(channelNodes, response, ctx));

  // Disable loading for all channels
  Array.from(validChannelMap.keys()).forEach((id) => {
    disableLoading(ctx, current_view_index, id);
  });
}

function processChannelGroupValues(
  channelNodes: DynamicFlatNode<IChannel>[],
  response: any,
  ctx: StateContext<MultiViewStateModel>
): (value: string, key: string, map: Map<string, string>) => void {
  return (label, id) => {
    const channelNode = channelNodes.find((node) => node.id === id);
    if (!channelNode) {
      console.warn(`Channel node not found for id: ${id}`);
      return;
    }
    const values = response.channelValues.find((cv: any) => cv.baseChannelGroup === channelNode.entity.parentIdentifier)?.values;
    if (!values) {
      console.warn(`No values found for channel id: ${id}`);
      return;
    }
    const channelSpecificValues = filterChannelValues(values, label) as TelemetryDataValues[];

    ctx.dispatch(new GetChannelComments(id));

    const channelResponse = createChannelResponse(id, channelSpecificValues, response);

    addToIndexedDB(id)(channelResponse);
  };
}

/**
 * Handles date range updates after successful data retrieval
 */
export function handleDateRangeUpdate(
  ctx: StateContext<MultiViewStateModel>,
  startDateValues: string | undefined,
  endDateValues: string | undefined,
  replaceDate: boolean
) {
  const currentState = ctx.getState();
  if (currentState?.views?.length) {
    ctx.setState(
      produce(currentState, (draft) => {
        const view = draft.views[getViewIndex(ctx)];
        if (view) {
          view.lastTelemetryFetchedAt = new Date();
        }
      })
    );
  }

  ctx.dispatch(GenerateEChartConfig).subscribe(() => {
    if (endDateValues && startDateValues && !replaceDate) {
      ctx
        .dispatch(
          new SetDateRangeByData({
            start_date: new Date(startDateValues),
            end_date: new Date(endDateValues),
          })
        )
        .subscribe(() => {
          ctx.dispatch(GenerateEChartConfig);
        });
    }
  });
}

/**
 * Checks and fetches missing channel data
 */
export async function checkAndFetchMissingData(channel: any, store: Store) {
  const control = new Control(channel);
  const mappingString = control.mappingString;

  if (!mappingString) {
    return;
  }

  const existingData = await db.channelData.get(mappingString);
  if (!existingData) {
    console.warn('fetching data for missing Channel', mappingString);
    await firstValueFrom(
      store.dispatch(
        new GetChannelData(channel.id, {
          windowSize: channel.windowSize,
          windowUnit: channel.windowUnit,
        })
      )
    );
  }
}

/**
 * Gets controls for export based on lane selection
 */
export function getControlsForExport(store: Store, laneIds?: string[]): StoreControl[] {
  if (laneIds && laneIds.length > 0) {
    // For multiple lanes, aggregate controls from all selected lanes
    const controls: StoreControl[] = [];
    for (const laneId of laneIds) {
      const laneControls = store.selectSnapshot(MultiViewState.getControlsForLane(laneId));
      controls.push(...laneControls);
    }
    // Remove duplicates
    return controls.filter((control, index, arr) => arr.findIndex((c) => c.id === control.id) === index);
  } else {
    // No filter, get all controls
    return store.selectSnapshot(MultiViewState.getSensorTableControlsOfCurrentView);
  }
}

/**
 * Exports data as CSV files in a ZIP archive
 */
export function exportAsCsv(data: any, nodes: DynamicFlatNode[], filename: string) {
  return import('jszip')
    .then(({ default: JSZip }) => {
      const zip = new JSZip();

      data.channelValues.forEach(({ values, baseChannelGroup }: any) => {
        const configuration = nodes.find(isSameControlAsId(baseChannelGroup));
        if (!configuration) {
          console.warn('no configuration found');
          return;
        }

        const instance = nodes.find(isSameControlAsId(configuration.entity.parentIdentifier));
        if (!instance) {
          console.warn('no instance found');
          return;
        }

        const reLabelValues = prefixObjectKeysWith(values, instance.label);
        zip.file(`${filename}_${baseChannelGroup}.csv`, convertJsonToCsv(JSON.stringify(reLabelValues)));
      });

      return zip.generateAsync({ type: 'blob' });
    })
    .then((content) => {
      if (content) {
        saveAs(content, `${filename}.zip`);
      }
    });
}

/**
 * Processes export data based on type
 */
export function processExportData(data: any, nodes: DynamicFlatNode[], type: string, filename: string) {
  if (type === 'csv') {
    exportAsCsv(data, nodes, filename);
  } else {
    exportDataAsJson(data, `${filename}.json`);
  }
}
